import React from 'react';
import { User } from '@nimbitlabs/features';
import { Badge, Button, Avatar, AvatarImage, AvatarFallback, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Skeleton, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@nimbitlabs/ui';
import { Edit, Trash2, Lock } from 'lucide-react';
import { useReadOnlyMode } from '@nimbitlabs/features';
import { useAuth } from '@nimbitlabs/auth';

interface UserTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onDelete: (userId: string) => void;
  loading?: boolean;
}

const formatDate = (date: Date | string | null | undefined) => {
  try {
    if (!date) {
      return 'N/A';
    }
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(dateObj);
  } catch {
    return 'N/A';
  }
};

const getStatusBadgeColor = (status: User['status']) => {
  switch (status) {
    case 'Active':
      return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 shadow-sm shadow-emerald-500/10';
    case 'Inactive':
      return 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30';
    default:
      return 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30';
  }
};

const getRoleBadgeColor = (role: User['role']) => {
  switch (role) {
    case 'Admin':
      return 'bg-destructive/10 text-destructive hover:bg-destructive/20';
    case 'Editor':
      return 'bg-primary/10 text-primary hover:bg-primary/20';
    case 'Viewer':
      return 'bg-muted text-muted-foreground hover:bg-muted/80';
    default:
      return 'bg-muted text-muted-foreground hover:bg-muted/80';
  }
};

export const UserTable: React.FC<UserTableProps> = ({ users, onEdit, onDelete, loading = false }) => {
  const { isReadOnly } = useReadOnlyMode();
  const { user: currentUser } = useAuth();
  
  return (
    <div className="bg-card rounded-lg shadow-soft border-0 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-border">
            <TableHead className="font-semibold text-foreground">User</TableHead>
            <TableHead className="font-semibold text-foreground">Role</TableHead>
            <TableHead className="font-semibold text-foreground">Status</TableHead>
            <TableHead className="font-semibold text-foreground">Created</TableHead>
            <TableHead className="font-semibold text-foreground">Last Active</TableHead>
            <TableHead className="font-semibold text-foreground w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell className="py-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-40" />
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : users.map((user) => (
            <TableRow key={user.id} className="hover:bg-muted/30 transition-colors">
              <TableCell className="py-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10 shadow-lg shadow-cyan-500/20 ring-2 ring-cyan-500/20">
                    <AvatarImage src={user.avatar} alt={user.name} />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white font-bold text-sm">
                      {user.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold text-foreground">{user.name}</div>
                    <div className="text-sm text-muted-foreground">{user.email}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Badge className={`${getRoleBadgeColor(user.role)} border-0 font-medium`}>
                    {user.role}
                  </Badge>
                  {user.isLocked && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="secondary" className="bg-warning/10 text-warning border-warning/20 cursor-help">
                            <Lock className="h-3 w-3 mr-1" />
                            Locked
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>This user has view-only access due to subscription limits.</p>
                          <p className="text-xs text-muted-foreground mt-1">Admins can still manage this user record.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={`${getStatusBadgeColor(user.status)} font-semibold`}>
                  {user.status}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(user.createdAt)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(user.lastActive)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(user)}
                    disabled={isReadOnly}
                    className="h-8 px-3"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  {currentUser?.email !== user.email && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(user.id)}
                      disabled={isReadOnly}
                      className="h-8 px-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!loading && users.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>No users found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};